import { object, string, array, z } from "zod";
import {  Role, Module } from '@prisma/client';

// Regex para validação de celular brasileiro
const cellPhoneRegex = /^(\+55|55)?(\d{2})?(9\d{8})$/;

// Novas definições de regex
const phoneRegex = /^(?:\(?\d{2}\)?\s?)?(?:9\d{4}|\d{4})\-?\d{4}$/;
const passwordRegex = /^(?=.*[A-Z])(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
const cnpjRegex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}\-\d{2}$/;
const cepRegex = /^\d{5}-?\d{3}$/;
const cpfRegex = /^\d{3}\.\d{3}\.\d{3}\-\d{2}$/;

export const signInSchema = object({
  email: string({ required_error: "Email is required" })
    .min(1, "Email is required")
    .email("Invalid email"),
  password: string({ required_error: "Password is required" })
    .min(1, "Password is required")
    .min(8, "Password must be more than 8 characters")
    .max(32, "Password must be less than 32 characters"),
})

export const registerSchema = object({
  name: string({ required_error: "Full name is required" })
    .min(1, "Full name is required")
    .max(100, "Full name must be less than 100 characters"),
  email: string({ required_error: "Email is required" })
    .min(1, "Email is required")
    .email("Invalid email"),
  password: string({ required_error: "Password is required" })
    .min(8, "Password must be at least 8 characters")
    .max(32, "Password must be less than 32 characters"),
  confirmPassword: string({ required_error: "Confirm password is required" })
    .min(1, "Confirm password is required"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
})

export const profileSchema = object({
  name: string().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  bio: string().max(500, "Bio must be less than 500 characters").optional(),
  avatarUrl: string().url("Invalid URL for avatar").optional(),
  jobTitle: string().max(100, "Job title must be less than 100 characters").optional(),
  department: string().max(100, "Department must be less than 100 characters").optional(),
  location: string().max(100, "Location must be less than 100 characters").optional(),
  mobile: string().regex(cellPhoneRegex, "Invalid cell phone number").optional(),
  privileges: array(string()),
  socialMediaLinks: z.record(string().url("Invalid URL for social media")).optional(),
  preferences: z.record(z.unknown()).optional(),
  access: z.array(z.nativeEnum(Module)).optional(),
})

export const userSchema = object({
  email: string().email("Invalid email"),
  password: string().min(8, "Password must be at least 8 characters"),
  role: z.enum(["USER", "ADMIN"]),
  emailVerified: z.date().nullable(),
  profile: profileSchema.optional(),
})

export const userEditSchema = z.object({
  id: z.string().optional(),
  email: z.string().email(),
  name: z.string(),
  role: z.nativeEnum(Role),
  mobile: z.string().nullable().optional(),
  jobTitle: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  modules: z.array(z.nativeEnum(Module)).default([]),
  privileges: z.array(z.string()).default([]),
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
  main_id: z.string(),
})

export const userAddSchema = z.object({
  email: z.string().email('Email inválido'),
  name: z.string().min(1, 'Nome é obrigatório'),
  privileges: z.array(z.string()),
  role: z.enum(['MASTER', 'USER', 'ADMIN']),
  password: z.string()
    .min(6, 'A senha deve ter pelo menos 6 caracteres')
    .regex(/[A-Z]/, 'A senha deve conter pelo menos uma letra maiúscula')
    .regex(/[a-z]/, 'A senha deve conter pelo menos uma letra minúscula')
    .regex(/[0-9]/, 'A senha deve conter pelo menos um número')
    .regex(/[^A-Za-z0-9]/, 'A senha deve conter pelo menos um caractere especial'),
  confirmPassword: z.string(),
  modules: z.array(z.nativeEnum(Module)).default([]),
  mobile: z.string().nullable().optional(),
  jobTitle: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  cnpj: z.string().optional(),
  cep: z.string().optional(),
  main_id: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não correspondem",
  path: ["confirmPassword"], // path of error
});

export type SignInSchemaType = z.infer<typeof signInSchema>;
export type RegisterSchemaType = z.infer<typeof registerSchema>;
export type ProfileSchemaType = z.infer<typeof profileSchema>;
export type UserSchemaType = z.infer<typeof userSchema>;
export type UserEditSchemaType = z.infer<typeof userEditSchema>;
export type UserAddSchemaType = z.infer<typeof userAddSchema>;